'use client';

import { c as _c } from "react/compiler-runtime";
import { usePrefetchGetMarketBackers, usePrefetchGetMarketTraders } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { BackerRewardsCardView } from '@/components/market/backer-rewards';
import { resolveBackerRewardsPlacement } from '@/components/market/backer-rewards/backer-rewards-placement';
import { MarketBackersTab } from '@/components/market/backers/market-backers-tab';
import { MarketDebateSection } from '@/components/market/market-debate-section';
import type { MarketDetailModel } from '@/components/market/market-detail-layout';
import { MarketDetailTabTransition } from '@/components/market/market-detail-tab-transition';
import { MarketInfoCard } from '@/components/market/market-info-card';
import { ResolvedPanel } from '@/components/market/resolved-panel';
import { MarketTradersTabContainer } from '@/components/market/traders/market-traders-tab-container';
import { DebateSkeleton } from '@/components/social/debate/debate-skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/app-tabs';
import { Panel } from '@/components/ui/panel';
import { useIntersectionObserver } from '@/hooks/ui/use-intersection-observer';
import { shouldMountMarketDetailTabContent } from '@/lib/market/market-detail-opening';
import { isMarketDetailTab, type MarketDetailTab, marketBackersTabQueryArgs, marketDetailTabOrder, marketTradersTabQueryArgs } from '@/lib/sdk/market-detail-tab-query';
const MARKET_TABS_VIEWPORT_MARGIN = '300px';
type TabPrefetch = (tab: MarketDetailTab) => void;
function useMarketDetailTabPrefetch(c) {
  const $ = _c(4);
  const prefetchTraders = usePrefetchGetMarketTraders();
  const prefetchBackers = usePrefetchGetMarketBackers();
  let t0;
  if ($[0] !== c || $[1] !== prefetchBackers || $[2] !== prefetchTraders) {
    t0 = tab => {
      bb2: switch (tab) {
        case "traders":
          {
            prefetchTraders(marketTradersTabQueryArgs(c.marketPubkey));
            break bb2;
          }
        case "backers":
          {
            if (!c.hasBackerRewards) {
              break bb2;
            }
            prefetchBackers(marketBackersTabQueryArgs(c.marketPubkey, c.tabSortParam));
            break bb2;
          }
        case "winner":
        case "debate":
        case "info":
      }
    };
    $[0] = c;
    $[1] = prefetchBackers;
    $[2] = prefetchTraders;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
}
function tabLabel(tab: MarketDetailTab, labels: Readonly<Record<MarketDetailTab, string>>): string {
  return labels[tab];
}
function MarketDetailTabTrigger(t0) {
  const $ = _c(8);
  const {
    tab,
    label,
    prefetchTab
  } = t0;
  const shouldPrefetch = tab === "traders" || tab === "backers";
  let t1;
  if ($[0] !== prefetchTab || $[1] !== shouldPrefetch || $[2] !== tab) {
    t1 = () => {
      if (shouldPrefetch) {
        prefetchTab(tab);
      }
    };
    $[0] = prefetchTab;
    $[1] = shouldPrefetch;
    $[2] = tab;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const warm = t1;
  let t2;
  if ($[4] !== label || $[5] !== tab || $[6] !== warm) {
    t2 = <TabsTrigger key={tab} value={tab} onMouseEnter={warm} onFocus={warm}>{label}</TabsTrigger>;
    $[4] = label;
    $[5] = tab;
    $[6] = warm;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function marketInfoProps(c: MarketDetailModel): React.ComponentProps<typeof MarketInfoCard> | null {
  const {
    market,
    marketRecord,
    creator,
    marketStatus,
    prizePool
  } = c;
  if (market === undefined) return null;
  return {
    market: {
      claimPool: String(market.prizePool ?? '0'),
      totalVolume: String(market.volumeTotal ?? '0'),
      volume24h: market.volume24h,
      volume7d: market.volume7d,
      totalReserves: String(market.totalReserves ?? '0'),
      uniqueTraders: Number(market.uniqueTradersTotal ?? 0),
      numOutcomes: Number(market.outcomeCount ?? 0),
      createdAt: String(market.createdAt ?? ''),
      description: market.description !== undefined && market.description !== null ? String(market.description) : null,
      category: market.category as string | null ?? null,
      consensusThreshold: c.thresholdConsumers.infoConsensusThreshold,
      boostTotal: marketRecord?.boostTotal !== undefined && marketRecord?.boostTotal !== null ? String(marketRecord.boostTotal) : null,
      numBoosters: marketRecord?.numBoosters !== undefined && marketRecord?.numBoosters !== null ? Number(marketRecord.numBoosters) : null
    },
    creator,
    parameters: {
      status: marketStatus,
      prizePool: prizePool.prizePoolD6
    }
  };
}
function MarketDetailTabContent(t0) {
  const $ = _c(13);
  const {
    c
  } = t0;
  switch (c.activeMarketSectionTab) {
    case "winner":
      {
        let t1;
        if ($[0] !== c) {
          t1 = <WinnerTabContent c={c} />;
          $[0] = c;
          $[1] = t1;
        } else {
          t1 = $[1];
        }
        return t1;
      }
    case "traders":
      {
        let t1;
        if ($[2] !== c) {
          t1 = <TradersTabContent c={c} />;
          $[2] = c;
          $[3] = t1;
        } else {
          t1 = $[3];
        }
        return t1;
      }
    case "info":
      {
        let t1;
        if ($[4] !== c) {
          t1 = marketInfoProps(c);
          $[4] = c;
          $[5] = t1;
        } else {
          t1 = $[5];
        }
        const props = t1;
        let t2;
        if ($[6] !== props) {
          t2 = props === null ? null : <MarketInfoCard {...props} />;
          $[6] = props;
          $[7] = t2;
        } else {
          t2 = $[7];
        }
        return t2;
      }
    case "debate":
      {
        let t1;
        if ($[8] !== c.marketPubkey || $[9] !== c.tradesFrom) {
          t1 = <MarketDebateSection marketPubkey={c.marketPubkey} tradesFrom={c.tradesFrom} />;
          $[8] = c.marketPubkey;
          $[9] = c.tradesFrom;
          $[10] = t1;
        } else {
          t1 = $[10];
        }
        return t1;
      }
    case "backers":
      {
        let t1;
        if ($[11] !== c) {
          t1 = c.hasBackerRewards ? <BackersTabContent c={c} /> : null;
          $[11] = c;
          $[12] = t1;
        } else {
          t1 = $[12];
        }
        return t1;
      }
  }
}
function WinnerTabContent(t0) {
  const $ = _c(9);
  const {
    c
  } = t0;
  if (c.winnerOutcomeIdx === null) {
    return null;
  }
  const t1 = c.market?.title ?? "";
  const t2 = typeof c.market?.resolvedAt === "string" ? c.market.resolvedAt : null;
  const t3 = typeof c.market?.volumeAtResolution === "string" ? c.market.volumeAtResolution : null;
  const t4 = typeof c.market?.uniqueTradersAtResolution === "number" ? c.market.uniqueTradersAtResolution : null;
  let t5;
  if ($[0] !== c.marketPubkey || $[1] !== c.marketStatus || $[2] !== c.resolvedPanelOutcomes || $[3] !== c.winnerOutcomeIdx || $[4] !== t1 || $[5] !== t2 || $[6] !== t3 || $[7] !== t4) {
    t5 = <ResolvedPanel variant="market-tab" marketPubkey={c.marketPubkey} marketTitle={t1} marketStatus={c.marketStatus} winnerOutcomeIndex={c.winnerOutcomeIdx} outcomes={c.resolvedPanelOutcomes} resolvedAtIso={t2} volumeAtResolutionRaw={t3} uniqueTradersAtResolution={t4} />;
    $[0] = c.marketPubkey;
    $[1] = c.marketStatus;
    $[2] = c.resolvedPanelOutcomes;
    $[3] = c.winnerOutcomeIdx;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
function TradersTabContent(t0) {
  const $ = _c(9);
  const {
    c
  } = t0;
  const t1 = c.marketPubkey;
  const t2 = c.onchainTradeData.data;
  const t3 = c.marketStatus === "graduated";
  let t4;
  if ($[0] !== c.outcomes) {
    t4 = c.outcomes ?? [];
    $[0] = c.outcomes;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== t4) {
    t5 = t4.map(_temp);
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== c.marketPubkey || $[5] !== c.onchainTradeData.data || $[6] !== t3 || $[7] !== t5) {
    t6 = <MarketTradersTabContainer marketPubkey={t1} poolSnapshot={t2} subscribeAmm={t3} outcomes={t5} />;
    $[4] = c.marketPubkey;
    $[5] = c.onchainTradeData.data;
    $[6] = t3;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
}
function _temp(outcome) {
  return {
    index: outcome.outcomeIndex,
    label: outcome.label,
    ...(outcome.imageUrl !== "" ? {
      imageUrl: outcome.imageUrl
    } : {}),
    imageNsfw: outcome.imageNsfw
  };
}
function BackersTabContent(t0) {
  const $ = _c(8);
  const {
    c
  } = t0;
  let t1;
  if ($[0] !== c.backerRewards) {
    t1 = resolveBackerRewardsPlacement(c.backerRewards.state) === "backers-tab" ? <BackerRewardsCardView {...c.backerRewards} className="max-w-none" /> : null;
    $[0] = c.backerRewards;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== c.backerRewardPool || $[3] !== c.marketPubkey) {
    t2 = <MarketBackersTab marketPubkey={c.marketPubkey} rewardPool={c.backerRewardPool} isActive={true} />;
    $[2] = c.backerRewardPool;
    $[3] = c.marketPubkey;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <div className="flex flex-col gap-4">{t1}{t2}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function MarketDetailTabBody(t0) {
  const $ = _c(3);
  const {
    c,
    contentMounted
  } = t0;
  if (!contentMounted) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div key="deferred" aria-hidden="true" className="max-h-140 overflow-hidden" data-testid="market-detail-tabs-deferred"><DebateSkeleton /></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== c) {
    t1 = <MarketDetailTabContent c={c} />;
    $[1] = c;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
export function MarketDetailTabs(t0) {
  const $ = _c(54);
  const {
    c
  } = t0;
  const t = useTranslations("market");
  const tBackers = useTranslations("market.backers");
  const prefetchTab = useMarketDetailTabPrefetch(c);
  const sectionRef = useRef(null);
  const [animateTabChanges, setAnimateTabChanges] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      once: true,
      rootMargin: MARKET_TABS_VIEWPORT_MARGIN
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    isIntersecting
  } = useIntersectionObserver(sectionRef, t1);
  let t2;
  if ($[1] !== c.activeMarketSectionTab || $[2] !== isIntersecting) {
    t2 = shouldMountMarketDetailTabContent(c.activeMarketSectionTab, isIntersecting);
    $[1] = c.activeMarketSectionTab;
    $[2] = isIntersecting;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const contentMounted = t2;
  let T0;
  let T1;
  let T2;
  let t10;
  let t11;
  let t12;
  let t13;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[4] !== c || $[5] !== prefetchTab || $[6] !== t || $[7] !== tBackers) {
    const labels = {
      winner: t("winner_tab"),
      debate: t("debate_tab"),
      traders: t("traders.tab_label"),
      backers: tBackers("tab_label"),
      info: t("info_tab")
    };
    T2 = Panel;
    t11 = "compact";
    t12 = "min-w-0 py-0";
    t13 = "market-detail-tabs";
    T1 = Tabs;
    t8 = "gap-4";
    t9 = c.activeMarketSectionTab;
    if ($[22] !== c || $[23] !== prefetchTab) {
      t10 = next => {
        if (isMarketDetailTab(next)) {
          setAnimateTabChanges(true);
          prefetchTab(next);
          c.setMarketSectionTabOverride(next);
        }
      };
      $[22] = c;
      $[23] = prefetchTab;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    t6 = sectionRef;
    t7 = "px-4 pt-4";
    T0 = TabsList;
    t3 = "segmented";
    t4 = "md";
    t5 = marketDetailTabOrder(c.marketStatus, c.hasBackerRewards).map(tab => <MarketDetailTabTrigger key={tab} tab={tab} label={tabLabel(tab, labels)} prefetchTab={prefetchTab} />);
    $[4] = c;
    $[5] = prefetchTab;
    $[6] = t;
    $[7] = tBackers;
    $[8] = T0;
    $[9] = T1;
    $[10] = T2;
    $[11] = t10;
    $[12] = t11;
    $[13] = t12;
    $[14] = t13;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    T0 = $[8];
    T1 = $[9];
    T2 = $[10];
    t10 = $[11];
    t11 = $[12];
    t12 = $[13];
    t13 = $[14];
    t3 = $[15];
    t4 = $[16];
    t5 = $[17];
    t6 = $[18];
    t7 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  let t14;
  if ($[25] !== T0 || $[26] !== t3 || $[27] !== t4 || $[28] !== t5) {
    t14 = <T0 variant={t3} size={t4}>{t5}</T0>;
    $[25] = T0;
    $[26] = t3;
    $[27] = t4;
    $[28] = t5;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t14 || $[31] !== t6 || $[32] !== t7) {
    t15 = <div ref={t6} className={t7}>{t14}</div>;
    $[30] = t14;
    $[31] = t6;
    $[32] = t7;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== c || $[35] !== contentMounted) {
    t16 = <MarketDetailTabBody c={c} contentMounted={contentMounted} />;
    $[34] = c;
    $[35] = contentMounted;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== animateTabChanges || $[38] !== c.activeMarketSectionTab || $[39] !== t16) {
    t17 = <div className="min-w-0 px-4 pb-4"><MarketDetailTabTransition activeTab={c.activeMarketSectionTab} animateChanges={animateTabChanges}>{t16}</MarketDetailTabTransition></div>;
    $[37] = animateTabChanges;
    $[38] = c.activeMarketSectionTab;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== T1 || $[42] !== t10 || $[43] !== t15 || $[44] !== t17 || $[45] !== t8 || $[46] !== t9) {
    t18 = <T1 className={t8} value={t9} onValueChange={t10}>{t15}{t17}</T1>;
    $[41] = T1;
    $[42] = t10;
    $[43] = t15;
    $[44] = t17;
    $[45] = t8;
    $[46] = t9;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== T2 || $[49] !== t11 || $[50] !== t12 || $[51] !== t13 || $[52] !== t18) {
    t19 = <T2 density={t11} className={t12} data-testid={t13}>{t18}</T2>;
    $[48] = T2;
    $[49] = t11;
    $[50] = t12;
    $[51] = t13;
    $[52] = t18;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  return t19;
}